import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import clsx from 'clsx';

import { isDesktop } from 'wtb-utils';
import { Button, Link, Loading, Typography } from 'wtb-components';
import { ReactComponent as DownloadImage } from 'assets/images/download.svg';
import { ReactComponent as DownloadIcon } from 'assets/images/download-icon.svg';
import { ReactComponent as WTBLogo } from 'assets/images/wtb-logo.svg';
import { ReactComponent as NoInvoice } from 'assets/images/no-invoice.svg';
import { getInvoiceById } from 'api';
import { Header } from 'components';
import {
  useLocaleContext,
  useTransactionContext,
  useAppContext
} from 'context';
import { getInvoicePdf } from 'api/invoice';
import { base64ToFileURL, isEmail } from 'utils';
import styles from './Download.module.css';

const DownloadPage = (props) => {
  const {
    setError
  } = props;

  const location = useLocation();
  const { id, file } = useParams();

  const { isLoading, setIsLoading } = useAppContext();
  const { merchant, setMerchant } = useTransactionContext();
  const {
    availableLanguages,
    changeLocale,
    translate
  } = useLocaleContext();

  const [ wtbTermsPage, setWtbTermsPage ] = useState(null);
  const [ initialized, setInitialized ] = useState(false);
  const [ fileData, setFileData ] = useState();

  useEffect(() => {
    if (!id || initialized || !availableLanguages) return;

    setInitialized(true);

    const downloadInvoice = () => {
      getInvoiceById(id)
        .then(async (data) => {
          changeLocale(data.language);
          setMerchant(data.merchant);
          setWtbTermsPage(data.termsPage);

          return getInvoicePdf(id);
        })
        .then((response) => { setFileData(response?.pdf); })
        .catch(() => setError(true))
        .finally(() => setIsLoading(false));
    };

    switch (file) {
      case 'invoice':
      default:
        downloadInvoice();
    }
  }, [
    id,
    file,
    availableLanguages,
    location.search,
    initialized,
    setInitialized,
    setMerchant,
    changeLocale,
    setIsLoading,
    setError
  ]);

  const download = () => {
    if (isDesktop) {
      window.open(base64ToFileURL(fileData));
    } else {
      const url = base64ToFileURL(fileData, 'application/pdf');
      const downloadLink = document.createElement('a');
      const fileName = `Invoice-${id}.pdf`;
      downloadLink.href = url;
      downloadLink.download = fileName;
      downloadLink.click();
    }
  };

  return (
    <div className={styles.body}>
      <Header />

      {isLoading && <div className={styles.loadingContainer}><Loading /></div>}

      {!isLoading && (
        <>
          <div className={styles.content}>
            <Typography variant="h6" component="h6">{translate(`download.${file}.title`)}</Typography>
            <DownloadImage className={styles.downloadImage} />
            <Typography variant="body1" component="p">{translate(`download.${file}.ready`)}</Typography>

            <Button
              type="button"
              iconPosition="left"
              className={styles.button}
              variant="text"
              icon={<DownloadIcon />}
              onClick={download}
            >
              {translate(`download.${file}.buttonText`)}
            </Button>
          </div>

          <div className={styles.bottomBox}>
            <div>
              <Typography variant="body2" component="span">{translate('invoiceEmail.poweredBy')}</Typography>
              <WTBLogo className={styles.wtbLogo} />
            </div>

            <Typography variant="body2" component="span">{translate('invoiceEmail.terms')}</Typography>

            <Link
              className={styles.link}
              href={merchant.termsPage}
              variant="body2"
              color="tertiary"
              role="link"
              underline="always"
              aria-label="merchant-link"
            >
              {merchant.name}
            </Link>
            <Link
              href={wtbTermsPage}
              variant="body2"
              color="tertiary"
              role="link"
              underline="always"
              className={styles.link}
            >
              Waytobill
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

const ErrorPage = () => {
  const { file } = useParams();

  const { merchant: { email, phone } } = useTransactionContext();
  const { translate } = useLocaleContext();

  return (
    <div className={styles.body}>
      <NoInvoice className={styles.noInvoice} />
      <Typography variant="h5" component="h5" className={styles.notFoundTitle}>
        {translate(`download.${file}.notFound`)}
      </Typography>
      <Typography variant="body1" component="p" align="center" className={styles.notFoundDescription}>
        {translate(`download.${file}.notFoundDescription`)}
      </Typography>
      {email && (
        <Typography
          variant="body2"
          component="a"
          href={isEmail(email) ? `mailto:${email}` : email}
          className={clsx(styles.link, styles.email)}
        >
          {email}
        </Typography>
      )}
      {phone && (
        <Typography
          variant="body2"
          component="a"
          href={`tel:${phone}`}
          className={styles.link}
        >
          {phone}
        </Typography>
      )}
    </div>
  );
};

export default function Download() {
  const [ error, setError ] = useState(false);

  return (
    <div className={styles.root}>
      {!error ? <DownloadPage setError={setError} /> : <ErrorPage />}
    </div>
  );
}
